import { z } from 'zod'
import type { SortingState, VisibilityState } from '@tanstack/vue-table'
import { InvoiceType, InvoiceStatusFilters } from '@/models/invoice.js'
import { PaymentMethod } from '@/models/payments.js'
import type {
  ReportFiltersState,
  ReportTotals,
} from '@/composables/useReportFiltersState.js'
import type { InvoiceReportItemModel } from '@/composables/api/InvoiceReport.js'
import type { NullableProps } from '@/utils/types.js'

// ============================================================================
// BASE TYPES
// ============================================================================

export type NormalizedInvoiceReportItem =
  NullableProps<InvoiceReportItemModel> & {
    subRows?: NormalizedInvoiceReportItem[]
  }

export type InvoiceReportPaymentStatuses =
  (typeof InvoiceReportPaymentStatus)[keyof typeof InvoiceReportPaymentStatus]
export const InvoiceReportPaymentStatus = {
  Paid: 'PAID',
  Unpaid: 'UNPAID',
  PartiallyPaid: 'PART_PAID',
} as const

// ============================================================================
// COLUMNS
// ============================================================================

export type InvoiceReportColumnIds =
  (typeof InvoiceReportColumns)[keyof typeof InvoiceReportColumns]['accessorKey']
export const InvoiceReportColumns = {
  InvoiceDate: {
    id: 'invoice_date',
    accessorKey: 'invoice_date',
  },
  InvoiceDueDate: {
    id: 'invoice_due_date',
    accessorKey: 'invoice_due_date',
  },
  InvoiceNumber: {
    id: 'invoice_number',
    accessorKey: 'invoice_number',
  },
  InvoiceStatus: {
    id: 'invoice_status',
    accessorKey: 'invoice_status',
  },
  InvoiceType: {
    id: 'invoice_type',
    accessorKey: 'invoice_type',
  },
  ConsultationNumber: {
    id: 'consultation_id',
    accessorKey: 'consultation_id',
  },
  ConsultationPatientType: {
    id: 'patient_type',
    accessorKey: 'patient_type',
  },
  Veterinarian: {
    id: 'supervising_vet_name_from_invoice',
    accessorKey: 'supervising_vet_name_from_invoice',
  },
  ClinicLocation: {
    id: 'department_name',
    accessorKey: 'department_name',
  },
  Client: {
    id: 'client_name',
    accessorKey: 'client_name',
  },
  TotalWithVat: {
    id: 'total_incl_vat',
    accessorKey: 'total_incl_vat',
  },
  TotalPaid: {
    id: 'amount_paid',
    accessorKey: 'amount_paid',
  },
  DueSum: {
    id: 'amount_due',
    accessorKey: 'amount_due',
  },
} as const satisfies Record<
  string,
  { id: string; accessorKey: keyof InvoiceReportItemModel }
>

/**
 * Default visible columns for invoice report
 */
export const InvoiceReportDefaultVisibleColumns = [
  InvoiceReportColumns.InvoiceDate.id,
  InvoiceReportColumns.InvoiceNumber.id,
  InvoiceReportColumns.InvoiceType.id,
  InvoiceReportColumns.ConsultationNumber.id,
  InvoiceReportColumns.ConsultationPatientType.id,
  InvoiceReportColumns.Veterinarian.id,
  InvoiceReportColumns.ClinicLocation.id,
  InvoiceReportColumns.Client.id,
  InvoiceReportColumns.TotalWithVat.id,
  InvoiceReportColumns.TotalPaid.id,
  InvoiceReportColumns.DueSum.id,
] as const

// ============================================================================
// GROUPING
// ============================================================================

export type InvoiceReportGroupableColumnIds = keyof Pick<
  InvoiceReportItemModel,
  | 'invoice_date'
  | 'client_name'
  | 'department_name'
  | 'supervising_vet_name_from_invoice'
>
export const InvoiceReportGroupableColumns = [
  'invoice_date',
  'client_name',
  'department_name',
  'supervising_vet_name_from_invoice',
] as const satisfies readonly InvoiceReportGroupableColumnIds[]

/**
 * Maps frontend field names to backend enum member names for the `report_data.group_by` parameter.
 * Used when building ShareReport and ScheduledReport payloads.
 * Backend expects enum members (CLIENT, DEPARTMENT, etc.), not field names.
 */
export const InvoiceReportGroupByFieldToEnum = {
  client_name: 'CLIENT_NAME',
  department_name: 'DEPARTMENT_NAME',
  invoice_date: 'INVOICE_DATE',
  supervising_vet_name_from_invoice: 'SUPERVISING_VET_NAME_FROM_INVOICE',
} as const

// ============================================================================
// SORTING
// ============================================================================

/**
 * Fields that can be used for sorting in the Groups API endpoint.
 * These are aggregated fields that apply to entire groups.
 */
export const InvoiceReportGroupAllowedSortFields: string[] = [
  InvoiceReportColumns.TotalWithVat.id,
  InvoiceReportColumns.TotalPaid.id,
  InvoiceReportColumns.DueSum.id,
]

// ============================================================================
// FILTERS
// ============================================================================

export type InvoiceReportFiltersIds =
  (typeof InvoiceReportFilters)[keyof typeof InvoiceReportFilters]
export const InvoiceReportFilters = {
  GroupBy: 'groupBy',
  Search: 'search',
  InvoiceDueDate: 'invoiceDueDate',
  InvoiceIssueDate: 'invoiceIssueDate',
  Veterinarian: 'veterinarian',
  ClinicLocation: 'clinicLocation',
  Client: 'client',
  PaymentMethod: 'paymentMethod',
  InvoiceType: 'invoiceType',
  PaymentStatus: 'paymentStatus',
  InvoiceStatus: 'invoiceStatus',
  InsuranceClaimStatus: 'insuranceClaimStatus',
  Tags: 'tags',
} as const

/**
 * Maps filter keys to their API query param names.
 * Date filters use 'range' suffix, others use 'in' suffix.
 */
export const InvoiceReportFilterApiParam = {
  [InvoiceReportFilters.Search]: 'filter[search]',
  [InvoiceReportFilters.InvoiceDueDate]: 'filter[invoice_due_date.range]',
  [InvoiceReportFilters.InvoiceIssueDate]: 'filter[invoice_date.range]',
  [InvoiceReportFilters.Veterinarian]:
    'filter[supervising_vet_id_from_invoice.in]',
  [InvoiceReportFilters.Client]: 'filter[client_id.in]',
  [InvoiceReportFilters.ClinicLocation]: 'filter[department_id.in]',
  [InvoiceReportFilters.InvoiceType]: 'filter[invoice_type.in]',
  [InvoiceReportFilters.InvoiceStatus]: 'filter[invoice_status.in]',
  [InvoiceReportFilters.PaymentStatus]: 'filter[payment_status.in]',
  [InvoiceReportFilters.PaymentMethod]: 'filter[payment_method.in]',
  [InvoiceReportFilters.InsuranceClaimStatus]:
    'filter[insurance_claim_status.in]',
  [InvoiceReportFilters.Tags]: 'filter[tags.in]',
} as const

/**
 * API query param names for group-specific filters used when drilling into expanded rows.
 * These accept single values to filter invoices within a specific group.
 */
export const InvoiceReportGroupFilterApiParam = {
  Client: 'filter[client_id.in]',
  ClinicLocation: 'filter[department_id.in]',
  Veterinarian: 'filter[supervising_vet_id_from_invoice.in]',
  InvoiceDate: 'filter[invoice_date.range]',
} as const

/**
 * Filter keys that represent date ranges (vs single dates or other filter types).
 * Used to determine which filters need special date formatting.
 */
export const InvoiceReportDateRangeFilters = [
  InvoiceReportFilters.InvoiceDueDate,
  InvoiceReportFilters.InvoiceIssueDate,
] as const

/**
 * Schema for invoice report filters
 */
export type InvoiceReportFiltersSchema = ReportFiltersState<
  typeof invoiceReportFiltersBaseSchema
>
export type InvoiceReportFiltersState = {
  filters: InvoiceReportFiltersSchema
  columnVisibility: VisibilityState
  sorting: SortingState
  totals?: ReportTotals
}

export const invoiceReportFiltersBaseSchema = z.object({
  [InvoiceReportFilters.GroupBy]: z.string().default(''),
  [InvoiceReportFilters.Search]: z.string().default(''),
  [InvoiceReportFilters.InvoiceDueDate]: z
    .tuple([z.string().date(), z.string().date()])
    .nullable()
    .default(null),
  [InvoiceReportFilters.InvoiceIssueDate]: z
    .tuple([z.string().date(), z.string().date()])
    .nullable()
    .default(null),
  [InvoiceReportFilters.Veterinarian]: z.array(z.string()).default([]),
  [InvoiceReportFilters.ClinicLocation]: z.array(z.string()).default([]),
  [InvoiceReportFilters.PaymentMethod]: z
    .array(z.enum(Object.values(PaymentMethod) as [string, ...string[]]))
    .default([]),
  [InvoiceReportFilters.InvoiceType]: z
    .array(z.enum(Object.values(InvoiceType) as [string, ...string[]]))
    .default([]),
  [InvoiceReportFilters.PaymentStatus]: z
    .array(
      z.enum(
        Object.values(InvoiceReportPaymentStatus) as [string, ...string[]],
      ),
    )
    .default([]),
  [InvoiceReportFilters.InvoiceStatus]: z
    .array(z.enum(Object.values(InvoiceStatusFilters) as [string, ...string[]]))
    .default([]),
  [InvoiceReportFilters.InsuranceClaimStatus]: z.array(z.string()).default([]),
  [InvoiceReportFilters.Tags]: z.array(z.string()).default([]),
  addedFilters: z.array(z.string()).default([]),
})

// ============================================================================
// API PAYLOAD TYPES (ShareReport/ScheduledReport)
// ============================================================================

/**
 * Types for the ShareReport and ScheduledReport API's `report_data` parameter.
 *
 * The `report_data` payload tells the backend which filters, sorting, field selection,
 * and grouping to apply when generating the shared report. It uses JSON:API query
 * parameter conventions with bracket notation (e.g., `filter[field.operation]`).
 *
 * These types are defined manually because the OpenAPI schema cannot properly represent
 * this nested, dynamic structure with bracket notation in property keys.
 */
type InvoiceReportDataPayloadFilters = {
  'fields[InvoiceReport]': string
} & Partial<{
  'filter[search]': string | string[]
  'filter[invoice_due_date.range]': string
  'filter[invoice_date.range]': string
  'filter[supervising_vet_id_from_invoice.in]': string | string[]
  'filter[department_id.in]': string | string[]
  'filter[invoice_type.in]': string | string[]
  'filter[invoice_status.in]': string | string[]
  'filter[payment_status.in]': string | string[]
  sort: string
}>
type InvoiceReportDataPayloadGroupBy =
  (typeof InvoiceReportGroupByFieldToEnum)[keyof typeof InvoiceReportGroupByFieldToEnum]
export type InvoiceReportDataPayload = {
  filters: InvoiceReportDataPayloadFilters
  group_by?: InvoiceReportDataPayloadGroupBy
}

// ============================================================================
// DEFAULTS & UTILITIES
// ============================================================================

// Fields to request from the Invoice report API
export const InvoiceReportDefaultFieldsRequest = {
  InvoiceReport: [
    'invoice_date',
    'invoice_due_date',
    'invoice_id',
    'invoice_number',
    'invoice_status',
    'invoice_type',
    'total_incl_vat',
    'amount_paid',
    'amount_due',
    'consultation_id',
    'patient_type',
    'client_name',
    'client_id',
    'supervising_vet_name_from_invoice',
    'department_name',
  ],
}

// Base object with all invoice report fields set to null
export const InvoiceReportEmptyItem = Object.fromEntries(
  InvoiceReportDefaultFieldsRequest.InvoiceReport.map((key) => [key, null]),
) as unknown as NormalizedInvoiceReportItem
